'use client';

import { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuthModal } from '@/context/AuthModalContext';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useEventStore } from '@/store/eventStore';
import { useUserStore } from '@/store/userStore';
import { AttendeeAvatars } from '@/components/events/AttendeeAvatars';
import { getStrapiImageUrl } from '@/lib/image-utils';
import { formatTime12H, parseDateOnly } from '@/utils';
import { welcomePageTranslations } from './translations';

// Toggle the right-rail "NewsSidebar" on/off. Default OFF until Andrew confirms.
// Flip to true to render the news-feed right rail beside the welcome content.
const SHOW_RIGHT_ASIDE = false;

export default function WelcomePage() {
  const { language } = useLanguage();
  const { openAuthModal } = useAuthModal();
  const { currentUser } = useAuth();
  const t = welcomePageTranslations[language];

  const { upcomingEvents, fetchUpcomingEvents } = useEventStore();
  const { users, fetchUsers } = useUserStore();

  useEffect(() => {
    fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  // The member directory is signed-in only: a signed-out request just gets a
  // 403, which the store logs and RUM reports as a JS error (#1473). Keyed on
  // the id so a session re-check that returns a fresh user object for the same
  // member does not reload the whole directory.
  useEffect(() => {
    if (currentUser?.id === undefined) return;
    fetchUsers();
  }, [currentUser?.id, fetchUsers]);

  const dateLocale = language === 'es' ? 'es-ES' : 'en-US';

  const main = (
    <div className="space-y-6">
      <h1
        className="text-[48px] leading-tight text-primary"
        style={{ fontFamily: 'var(--font-abhaya-libre)' }}
      >
        {t.title}
      </h1>

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        {/* Hero — Figma source-of-truth is 1078x690.
            Desktop: pixel-perfect canvas locked to that aspect ratio, all children
            positioned via % derived from Figma coords so it scales proportionally.
            Mobile: separate stacked layout below. */}
        <section className="relative overflow-hidden" data-testid="welcome-hero">
          {/* DESKTOP */}
          <div className="hidden md:block relative" style={{ aspectRatio: '1078 / 690', backgroundColor: '#00423E' }}>
            {/* Crowd photo sits BENEATH the SVG decorations so the sage egg overlaps it (Figma layering) */}
            <div className="absolute pointer-events-none" style={{ top: '0%', left: '0%', width: '24.954%', height: '47.246%' }} aria-hidden>
              <Image src="/welcome-hero-crowd.png" alt="" fill className="object-contain object-left-top" />
            </div>
            {/* Eight decorative blob paths exported from Figma (no green rect — that's the canvas bg) */}
            <Image
              src="/welcome-hero-decorations.svg"
              alt=""
              fill
              priority
              className="object-cover pointer-events-none select-none"
              aria-hidden
            />
            {/* Women photo — Figma x=36, y=485, w=283.76, h=205.21 */}
            <div className="absolute pointer-events-none" style={{ top: '70.290%', left: '3.340%', width: '26.323%', height: '29.741%' }} aria-hidden>
              <Image src="/welcome-hero-women.png" alt="" fill className="object-contain object-left-top" />
            </div>
            {/* Walker photo — Figma x=841, y=206, w=237, h=414 */}
            <div className="absolute pointer-events-none" style={{ top: '29.855%', left: '78.014%', width: '21.985%', height: '60.000%' }} aria-hidden>
              <Image src="/welcome-hero-walker.png" alt="" fill className="object-contain object-left-top" />
            </div>
            {/* Sage bottom-right blob — Figma layers this ON TOP of the walker photo. */}
            <Image
              src="/welcome-hero-blob-bottom-right-desktop.svg"
              alt=""
              fill
              className="object-cover pointer-events-none select-none"
              aria-hidden
            />
            {/* Logo — Figma x=447, y=66, w=184, h=140 */}
            <div className="absolute" style={{ top: '9.565%', left: '41.466%', width: '17.069%', height: '20.290%' }}>
              <Image src="/welcome-hero-logo.png" alt={t.hero.logoAlt} fill priority className="object-contain object-left-top" />
            </div>
            {/* Text block — Figma x=302, y=246, w=474 */}
            <div
              className="absolute flex flex-col items-center text-center"
              style={{ top: '35.652%', left: '28.014%', width: '43.970%' }}
            >
              <h2
                className="text-[44px] leading-tight mb-6"
                style={{ fontFamily: 'var(--font-abhaya-libre)', color: '#F0E5D6' }}
              >
                {t.hero.headline}
              </h2>
              <p className="text-lg leading-relaxed mb-8" style={{ color: '#F0E5D6' }}>
                {t.hero.body}
              </p>
              <Link
                href="/about"
                className="inline-flex items-center justify-center px-10 py-2.5 rounded-full text-sm font-bold tracking-[2px] uppercase border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#00423E]"
                style={{ borderColor: '#F0E5D6', color: '#F0E5D6' }}
                data-testid="welcome-hero-cta"
              >
                {t.hero.cta}
              </Link>
            </div>
          </div>

          {/* MOBILE — Figma source-of-truth is iPhone Welcome 2 / Frame 57 (428x694).
              The Figma frame had 30px of empty green below the bottom cluster; we pin the
              photo flush to the bottom and shift the bottom-left olive + bottom-right sage
              paths down by 30px inside the SVG (transform group) so the whole bottom cluster
              sits flush against the hero's bottom edge. Top + mid-left blobs stay put. */}
          <div
            className="md:hidden relative overflow-hidden"
            style={{ aspectRatio: '428 / 694', backgroundColor: '#00423E' }}
          >
            {/* BEHIND-photo decorations: diagonal overlay, top-right cluster, mid-left
                sage+slate (mid-left shifted down 30px to track the photo). */}
            <Image
              src="/welcome-hero-decorations-mobile.svg"
              alt=""
              fill
              priority
              className="object-cover pointer-events-none select-none"
              aria-hidden
            />
            {/* Women photo — Figma w=281, h=203, pinned flush to bottom (replaces the
                Figma y=461 which left a 30px green strip below). */}
            <div
              className="absolute pointer-events-none"
              style={{ bottom: '0%', left: '9.346%', width: '65.654%', height: '29.251%' }}
              aria-hidden
            >
              <Image src="/welcome-hero-women.png" alt="" fill className="object-contain object-left-top" />
            </div>
            {/* OVER-photo decorations: bottom-left olive + bottom-right sage, both shifted
                down 30px. Rendered AFTER the photo so they overlap the photo edges (Figma). */}
            <Image
              src="/welcome-hero-blob-olive-mobile.svg"
              alt=""
              fill
              className="object-cover pointer-events-none select-none"
              aria-hidden
            />
            {/* Text block — Figma x=40, y=62, w=285, left-aligned, 30px gap between children */}
            <div
              className="absolute flex flex-col items-start text-left"
              style={{ top: '8.934%', left: '9.346%', width: '66.589%' }}
            >
              <h2
                className="text-[28px] leading-tight"
                style={{ fontFamily: 'var(--font-abhaya-libre)', color: '#F0E5D6', marginBottom: '30px' }}
              >
                {t.hero.headline}
              </h2>
              <p
                className="text-[15px] leading-relaxed"
                style={{ color: '#F0E5D6', marginBottom: '30px' }}
              >
                {t.hero.body}
              </p>
              <Link
                href="/about"
                className="inline-flex items-center justify-center px-8 py-2.5 rounded-full text-xs font-bold tracking-[2px] uppercase border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#00423E]"
                style={{ borderColor: '#F0E5D6', color: '#F0E5D6' }}
              >
                {t.hero.cta}
              </Link>
            </div>
          </div>
        </section>

        {/* Welcome to Our Online Community + Quick Links */}
        <section className="px-6 md:px-12 py-10 md:py-12">
          <div className="flex flex-col md:flex-row gap-8 md:gap-20">
            {/* Left: heading + paragraphs */}
            <div className="flex-1 space-y-4 md:max-w-[493px]">
              <h2
                className="text-[30px] md:text-[44px] leading-tight text-primary"
                style={{ fontFamily: 'var(--font-abhaya-libre)' }}
              >
                {t.welcomeSection.title}
              </h2>
              <p className="text-lg text-primary leading-relaxed font-bold">
                {t.welcomeSection.p1}
              </p>
              <p className="text-base text-primary leading-relaxed">
                {t.welcomeSection.p2}
              </p>
              <p className="text-base text-primary leading-relaxed">
                {t.welcomeSection.p3}
              </p>

              {/* Mobile-only View Our Work button (matches Figma mobile spec) */}
              <Link
                href="/our-work"
                className="md:hidden mt-6 w-full inline-flex items-center justify-center px-10 py-2.5 border border-primary rounded-full text-xs font-bold tracking-[2px] uppercase text-primary hover:bg-primary hover:text-white transition"
                data-testid="welcome-view-our-work"
              >
                {t.welcomeSection.viewOurWork}
              </Link>
            </div>

            {/* Right: Quick Links panel */}
            <aside
              className="w-full md:w-[380px] rounded-[15px] p-[30px] md:pl-[45px] md:pt-[45px]"
              style={{ backgroundColor: 'rgba(171, 189, 186, 0.2)' }}
              data-testid="welcome-quick-links"
            >
              <h3
                className="text-[18px] font-bold uppercase tracking-[2px] mb-5"
                style={{ color: '#78823D' }}
              >
                {t.quickLinks.title}
              </h3>
              <ul className="space-y-5">
                <li>
                  <button
                    type="button"
                    onClick={() => openAuthModal('signup')}
                    className="flex items-center gap-2 text-[22px] font-bold text-primary hover:opacity-70 transition"
                    data-testid="welcome-quick-link-sign-up"
                  >
                    <ArrowUpRight size={24} strokeWidth={2.5} className="shrink-0" />
                    <span>{t.quickLinks.signUpLabel}</span>
                  </button>
                </li>
                {t.quickLinks.items.map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="flex items-center gap-2 text-[22px] font-bold text-primary hover:opacity-70 transition"
                    >
                      <ArrowUpRight size={24} strokeWidth={2.5} className="shrink-0" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </section>

        {/* Divider */}
        <div className="px-6 md:px-12">
          <div className="border-t border-primary" />
        </div>

        {/* Upcoming Local Events */}
        <section className="px-6 md:px-12 py-10 md:py-12 space-y-8 md:space-y-11">
          <h2
            className="text-[30px] md:text-[44px] leading-tight text-primary"
            style={{ fontFamily: 'var(--font-abhaya-libre)' }}
          >
            {t.events.title}
          </h2>

          {upcomingEvents.length === 0 ? (
            <p className="text-base text-foreground-muted">{t.events.empty}</p>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-11"
              data-testid="welcome-events-grid"
            >
              {upcomingEvents.slice(0, 3).map((event) => {
                // parseDateOnly avoids UTC-midnight shifting YYYY-MM-DD into the previous day in MT.
                const eventDate = parseDateOnly(event.date);
                const dayShort = eventDate.toLocaleDateString(dateLocale, {
                  weekday: 'short',
                });
                const monthShort = eventDate.toLocaleDateString(dateLocale, {
                  month: 'short',
                });
                const day = eventDate.getDate();
                const time = formatTime12H(event.timeStart);
                const imgUrl = event.image?.url ? getStrapiImageUrl(event.image.url) : null;

                return (
                  <Link
                    key={event.documentId}
                    href={`/events/${event.documentId}`}
                    className="block group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    data-testid="welcome-event-card"
                  >
                    <div className="rounded-[10px] overflow-hidden bg-background-secondary aspect-[300/224] mb-4">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt=""
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition"
                        />
                      ) : null}
                    </div>
                    <h3 className="text-[22px] font-bold text-primary leading-tight line-clamp-2 mb-2">
                      {event.title}
                    </h3>
                    <p className="text-[21px] text-primary leading-snug mb-3">
                      {`${dayShort}, ${monthShort} ${day}`}
                      {time ? ` | ${time}` : ''}
                    </p>
                    <AttendeeAvatars
                      attendees={event.attendees || []}
                      users={users}
                      maxVisible={3}
                      size="sm"
                      countLabel={t.events.attendingShort}
                    />
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Create Your Space CTA — full-width lighter tan box. The surrounding darker tan
            lives on the outer page wrapper (see return below), so this section sits flush
            with the white card's edges. */}
        <section
          className="overflow-hidden"
          style={{ backgroundColor: '#F7F2EB' }}
          data-testid="welcome-cta"
        >
          <div className="flex flex-col md:flex-row md:items-end gap-8 md:gap-11 p-8 md:p-12">
            <div className="md:w-[432px] space-y-6">
              <h2
                className="text-[30px] md:text-[44px] leading-tight text-primary"
                style={{ fontFamily: 'var(--font-abhaya-libre)' }}
              >
                {t.cta.title}
              </h2>
              <p className="text-base md:text-lg text-primary leading-relaxed">
                {t.cta.body}
              </p>
              <button
                type="button"
                onClick={() => openAuthModal('signup')}
                className="inline-flex items-center justify-center px-10 py-2.5 border border-primary rounded-full text-xs md:text-sm font-bold tracking-[2px] uppercase text-primary hover:bg-primary hover:text-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                data-testid="welcome-signup-cta"
              >
                {t.cta.button}
              </button>
            </div>
            <div className="md:flex-1 -mr-8 md:-mr-12 -mb-8 md:-mb-12">
              <Image
                src="/welcome-cta-picnic.png"
                alt={t.cta.imageAlt}
                width={556}
                height={333}
                className="w-full h-auto block"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  // Outer page wrapper = darker tan (Figma's Column 10 bg).
  // This is the "container above" that the CTA's earlier mx-12 used to expose;
  // now darker tan shows around the entire white welcome card on all sides.
  if (!SHOW_RIGHT_ASIDE) {
    return <div className="p-3 md:p-6 bg-background-secondary">{main}</div>;
  }

  // Right-aside ON: wrap main + NewsSidebar in a flex row.
  // (Andrew may flip this on after MWBI-602 review.)
  return (
    <div className="p-3 md:p-6 flex gap-6 bg-background-secondary">
      <div className="flex-1 min-w-0">{main}</div>
    </div>
  );
}
